import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type GetThesisDetailResType, type ThesisItem, useGetThesisApi, useGetThesisDetailApi } from '@/api/thesis';
import ModalBox from '@/components/common/Modal/ModalBox/ModalBox';
import TablePagination from '@/components/common/Pagination/TablePagination';

import * as S from './style';

const PAGE_SIZE = 10;
const FETCH_SIZE = 500;
const YEAR_OPTIONS = Array.from({ length: 57 }, (_, index) => 2026 - index);

type SearchFilters = {
	startYear: string;
	endYear: string;
	volume: string;
	issue: string;
	title: string;
	author: string;
	keyword: string;
	abstract: string;
};

const DEFAULT_FILTERS: SearchFilters = {
	startYear: '1970',
	endYear: '2026',
	volume: '',
	issue: '',
	title: '',
	author: '',
	keyword: '',
	abstract: '',
};

const normalizeThesisResponse = (data?: {
	thesis?: ThesisItem[];
	list?: ThesisItem[];
	items?: ThesisItem[];
	total?: number;
	total_count?: number;
	thesis_count?: number;
	totalCount?: number;
}) => data?.thesis ?? data?.list ?? data?.items ?? [];

const normalizeThesisDetailResponse = (data?: GetThesisDetailResType | null) => {
	if (!data) return null;
	return data.thesis ?? data.detail ?? data.item ?? data;
};

const getStringValue = (item: ThesisItem, keys: string[]) => {
	const record = item as Record<string, unknown>;

	for (const key of keys) {
		const value = record[key];
		if (typeof value === 'string' && value.trim()) {
			return value.trim();
		}
		if (typeof value === 'number') {
			return String(value);
		}
	}

	return '';
};

const getRecordStringValue = (value: unknown, keys: string[]) => {
	if (!value || typeof value !== 'object') return '';

	const record = value as Record<string, unknown>;

	for (const key of keys) {
		const fieldValue = record[key];
		if (typeof fieldValue === 'string' && fieldValue.trim()) {
			return fieldValue.trim();
		}
		if (typeof fieldValue === 'number') {
			return String(fieldValue);
		}
	}

	return '';
};

const getNumberValue = (item: ThesisItem, keys: string[]) => {
	const value = getStringValue(item, keys);
	return value ? Number(value) : Number.NaN;
};

const getPublishYear = (item: ThesisItem) => {
	const directYear = getStringValue(item, ['thesis_year']);
	if (directYear) return directYear;

	const publishDate = getStringValue(item, ['thesis_publish_date']);
	if (!publishDate) return '';

	return publishDate.slice(0, 4);
};

const getVolume = (item: ThesisItem) => getStringValue(item, ['thesis_volume']);
const getIssue = (item: ThesisItem) => getStringValue(item, ['thesis_issue']);
const getAuthors = (item: ThesisItem) => getStringValue(item, ['thesis_author']);

const getDownloadUrl = (filePath: string) => {
	if (!filePath) return '';
	if (/^https?:\/\//i.test(filePath)) return filePath;
	return `https://api-kocsa.devermall.com/api/v1${filePath.startsWith('/') ? filePath : `/${filePath}`}`;
};

const getTitlePair = (item: ThesisItem, emptyTitle: string) => {
	const primaryTitle = getStringValue(item, ['thesis_title']) || emptyTitle;

	return {
		primaryTitle,
		secondaryTitle: '',
	};
};

const getSortWeight = (item: ThesisItem) => {
	const year = Number(getPublishYear(item) || 0);
	const volume = getNumberValue(item, ['thesis_volume']);
	const issue = getNumberValue(item, ['thesis_issue']);
	return {
		year,
		volume: Number.isFinite(volume) ? volume : 0,
		issue: Number.isFinite(issue) ? issue : 0,
	};
};

const sortItemsDesc = (items: ThesisItem[]) =>
	[...items].sort((a, b) => {
		const left = getSortWeight(a);
		const right = getSortWeight(b);
		if (left.year !== right.year) return right.year - left.year;
		if (left.volume !== right.volume) return right.volume - left.volume;
		if (left.issue !== right.issue) return right.issue - left.issue;
		return 0;
	});

const normalizeRequestFilters = (filters: SearchFilters, searchTriggered: boolean) => ({
	start_year: searchTriggered && filters.startYear !== DEFAULT_FILTERS.startYear ? filters.startYear : undefined,
	end_year: searchTriggered && filters.endYear !== DEFAULT_FILTERS.endYear ? filters.endYear : undefined,
	volume: searchTriggered && filters.volume ? filters.volume : undefined,
	issue: searchTriggered && filters.issue ? filters.issue : undefined,
	title: searchTriggered && filters.title.trim() ? filters.title.trim() : undefined,
	author: searchTriggered && filters.author.trim() ? filters.author.trim() : undefined,
	keyword: searchTriggered && filters.keyword.trim() ? filters.keyword.trim() : undefined,
	abstract: searchTriggered && filters.abstract.trim() ? filters.abstract.trim() : undefined,
});

const PublicationJournalSearch: React.FC = () => {
	const { t } = useTranslation();
	const [page, setPage] = useState(1);
	const [filters, setFilters] = useState<SearchFilters>(DEFAULT_FILTERS);
	const [submittedFilters, setSubmittedFilters] = useState<SearchFilters>(DEFAULT_FILTERS);
	const [searchTriggered, setSearchTriggered] = useState(false);
	const [selectedThesis, setSelectedThesis] = useState<{ thesisPk: string; title: string } | null>(null);

	const queryFilters = useMemo(() => normalizeRequestFilters(submittedFilters, searchTriggered), [submittedFilters, searchTriggered]);

	const { data: optionThesisData } = useGetThesisApi({
		page: 1,
		row: FETCH_SIZE,
	});

	const { data: thesisData } = useGetThesisApi({
		...queryFilters,
		page,
		row: PAGE_SIZE,
	});
	const { data: thesisDetailData, isLoading: isThesisDetailLoading } = useGetThesisDetailApi(
		{
			thesis_pk: selectedThesis?.thesisPk ?? '',
		},
		Boolean(selectedThesis?.thesisPk)
	);

	const emptyTitle = t('publication.search.emptyTitle');
	const optionItems = useMemo(() => sortItemsDesc(normalizeThesisResponse(optionThesisData)), [optionThesisData]);
	const pagedItems = useMemo(() => sortItemsDesc(normalizeThesisResponse(thesisData)), [thesisData]);
	const thesisDetail = useMemo(() => normalizeThesisDetailResponse(thesisDetailData), [thesisDetailData]);
	const primaryDetailFile = thesisDetailData?.file?.[0];
	const totalItems = Number(thesisData?.thesis_count ?? thesisData?.total_count ?? thesisData?.totalCount ?? thesisData?.total ?? pagedItems.length);
	const detailTitle = getRecordStringValue(thesisDetail, ['thesis_title', 'title']) || selectedThesis?.title || emptyTitle;
	const detailContent = getRecordStringValue(thesisDetail, ['thesis_content', 'content', 'thesis_abstract', 'abstract']);
	const detailContentHtml = /<\/?[a-z][\s\S]*>/i.test(detailContent) ? detailContent : detailContent.replace(/\n/g, '<br />');
	const detailFilePk = primaryDetailFile?.file_pk ? String(primaryDetailFile.file_pk) : '';

	const volumeOptions = useMemo(
		() => Array.from(new Set(optionItems.map((item) => getVolume(item)).filter(Boolean))).sort((a, b) => Number(b) - Number(a)),
		[optionItems]
	);

	const issueOptions = useMemo(() => {
		const candidates = filters.volume ? optionItems.filter((item) => getVolume(item) === filters.volume) : optionItems;
		return Array.from(new Set(candidates.map((item) => getIssue(item)).filter(Boolean))).sort((a, b) => Number(a) - Number(b));
	}, [optionItems, filters.volume]);

	const hasActiveFilters = useMemo(
		() =>
			searchTriggered &&
			Object.entries(submittedFilters).some(([key, value]) => {
				if (key === 'startYear') return value !== DEFAULT_FILTERS.startYear;
				if (key === 'endYear') return value !== DEFAULT_FILTERS.endYear;
				return value.trim() !== '';
			}),
		[searchTriggered, submittedFilters]
	);

	useEffect(() => {
		setPage(1);
	}, [submittedFilters]);

	const handleFieldChange = <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
	};

	const handleSearch = () => {
		setSubmittedFilters(filters);
		setSearchTriggered(true);
	};

	const handleInputEnter = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleSearch();
		}
	};

	const handleOpenAbstract = (item: ThesisItem) => {
		const thesisPk = getStringValue(item, ['thesis_pk']);
		if (!thesisPk) return;

		setSelectedThesis({
			thesisPk,
			title: getTitlePair(item, emptyTitle).primaryTitle,
		});
	};

	const triggerPdfDownload = (downloadUrl: string, fileName: string) => {
		if (!downloadUrl) return;

		const anchor = document.createElement('a');
		anchor.href = downloadUrl;
		anchor.download = fileName;
		anchor.target = '_blank';
		anchor.rel = 'noopener noreferrer';
		document.body.appendChild(anchor);
		anchor.click();
		anchor.remove();
	};

	const handleDownloadPdf = (item: ThesisItem) => {
		const downloadUrl = getDownloadUrl(getStringValue(item, ['file_path']));
		const fileName = getStringValue(item, ['file_original_name']) || getTitlePair(item, emptyTitle).primaryTitle;

		triggerPdfDownload(downloadUrl, fileName);
	};

	const handleDownloadDetailPdf = () => {
		if (!detailFilePk) return;

		window.open(`https://api-kocsa.devermall.com/api/v1/common/fileDownload?file_pk=${detailFilePk}`);
	};

	return (
		<S.Container>
			<S.SectionTitle>{t('publication.search.title')}</S.SectionTitle>

			<S.SearchPanel>
				<S.SearchGrid>
					<S.SearchRow>
						<S.SearchLabel>{t('publication.search.form.years')}</S.SearchLabel>
						<S.YearControls>
							<S.Select value={filters.startYear} onChange={(e) => handleFieldChange('startYear', e.target.value)}>
								{YEAR_OPTIONS.map((year) => (
									<option key={`start-${year}`} value={String(year)}>
										{year}
									</option>
								))}
							</S.Select>
							<S.YearDivider>{t('publication.search.form.to')}</S.YearDivider>
							<S.Select value={filters.endYear} onChange={(e) => handleFieldChange('endYear', e.target.value)}>
								{YEAR_OPTIONS.map((year) => (
									<option key={`end-${year}`} value={String(year)}>
										{year}
									</option>
								))}
							</S.Select>
						</S.YearControls>
					</S.SearchRow>

					<S.SearchRow>
						<S.SearchLabel>{t('publication.search.form.volumeIssue')}</S.SearchLabel>
						<S.DoubleControls>
							<S.Select value={filters.volume} onChange={(e) => handleFieldChange('volume', e.target.value)}>
								<option value="">{t('publication.search.form.volumePlaceholder')}</option>
								{volumeOptions.map((volume) => (
									<option key={volume} value={volume}>
										{volume}
									</option>
								))}
							</S.Select>
							<S.Select value={filters.issue} onChange={(e) => handleFieldChange('issue', e.target.value)}>
								<option value="">{t('publication.search.form.issuePlaceholder')}</option>
								{issueOptions.map((issue) => (
									<option key={issue} value={issue}>
										{issue}
									</option>
								))}
							</S.Select>
						</S.DoubleControls>
					</S.SearchRow>

					<S.SearchRow>
						<S.SearchLabel>{t('publication.search.form.title')}</S.SearchLabel>
						<S.Input
							value={filters.title}
							onChange={(e) => handleFieldChange('title', e.target.value)}
							onKeyDown={handleInputEnter}
							placeholder={t('publication.search.form.titlePlaceholder')}
						/>
					</S.SearchRow>

					<S.SearchRow>
						<S.SearchLabel>{t('publication.search.form.author')}</S.SearchLabel>
						<S.Input
							value={filters.author}
							onChange={(e) => handleFieldChange('author', e.target.value)}
							onKeyDown={handleInputEnter}
							placeholder={t('publication.search.form.authorPlaceholder')}
						/>
					</S.SearchRow>

					<S.SearchRow>
						<S.SearchLabel>{t('publication.search.form.keyword')}</S.SearchLabel>
						<S.Input
							value={filters.keyword}
							onChange={(e) => handleFieldChange('keyword', e.target.value)}
							onKeyDown={handleInputEnter}
							placeholder={t('publication.search.form.keywordPlaceholder')}
						/>
					</S.SearchRow>

					<S.SearchRow>
						<S.SearchLabel>{t('publication.search.form.abstract')}</S.SearchLabel>
						<S.Input
							value={filters.abstract}
							onChange={(e) => handleFieldChange('abstract', e.target.value)}
							onKeyDown={handleInputEnter}
							placeholder={t('publication.search.form.abstractPlaceholder')}
						/>
					</S.SearchRow>
				</S.SearchGrid>

				<S.SearchButtonRow>
					<S.SearchButton type="button" onClick={handleSearch}>
						{t('publication.search.button')}
					</S.SearchButton>
				</S.SearchButtonRow>
			</S.SearchPanel>

			<S.ResultSection>
				<S.ResultTitle>{hasActiveFilters ? t('publication.search.searchResults') : t('publication.search.listTitle')}</S.ResultTitle>

				{pagedItems.length > 0 ? (
					<TablePagination activePage={page} itemsPerPage={PAGE_SIZE} totalItems={totalItems} onChange={setPage}>
						<S.ResultList>
							{pagedItems.map((item, index) => {
								const { primaryTitle, secondaryTitle } = getTitlePair(item, emptyTitle);
								const authors = getAuthors(item);
								const thesisPk = getStringValue(item, ['thesis_pk']);
								const year = getPublishYear(item);
								const volume = getVolume(item);
								const issue = getIssue(item);
								const pdfUrl = getDownloadUrl(getStringValue(item, ['file_path']));
								const metaSegments = [`${volume || '-'}(${issue || '-'})`, year || '-'].filter(Boolean);

								return (
									<S.ResultItem key={`${item.thesis_pk}-${index}`}>
										<S.ArticleTitle>{primaryTitle}</S.ArticleTitle>
										{secondaryTitle ? <S.ArticleSubtitle>{secondaryTitle}</S.ArticleSubtitle> : null}
										{authors ? <S.ArticleAuthors>{authors}</S.ArticleAuthors> : null}
										<S.ArticleMeta>{metaSegments.join(' ')}</S.ArticleMeta>
										<S.ActionRow>
											<S.ActionButton type="button" onClick={() => handleDownloadPdf(item)} disabled={!pdfUrl}>
												{t('publication.search.buttons.pdf')}
											</S.ActionButton>
											<S.ActionButton type="button" onClick={() => handleOpenAbstract(item)} disabled={!thesisPk}>
												{t('publication.search.buttons.html')}
											</S.ActionButton>
										</S.ActionRow>
									</S.ResultItem>
								);
							})}
						</S.ResultList>
					</TablePagination>
				) : (
					<S.EmptyState>{t('publication.search.emptyState')}</S.EmptyState>
				)}
			</S.ResultSection>

			{selectedThesis ? (
				<ModalBox onClick={() => setSelectedThesis(null)} title={detailTitle} isCloseButton maxWidth="90vw" minHeight="90vh">
					<S.AbstractModalContent>
						{isThesisDetailLoading ? (
							<S.AbstractModalBody>{t('publication.search.detailLoading')}</S.AbstractModalBody>
						) : detailContentHtml ? (
							<S.AbstractModalBody dangerouslySetInnerHTML={{ __html: detailContentHtml }} />
						) : (
							<S.AbstractModalBody>{t('publication.search.detailEmpty')}</S.AbstractModalBody>
						)}
						<S.ModalActionRow>
							<S.ActionButton type="button" onClick={handleDownloadDetailPdf} disabled={!detailFilePk}>
								{t('publication.search.buttons.pdfDownload')}
							</S.ActionButton>
						</S.ModalActionRow>
					</S.AbstractModalContent>
				</ModalBox>
			) : null}
		</S.Container>
	);
};

export default PublicationJournalSearch;
